/**
 * Utilitaires pour les transformations de données immobilières
 *
 * Centralise les transformations répétitives entre les formats frontend/backend
 * pour éviter la duplication de code dans les mappers et payloads.
 */
import { OwnerDto } from '@app/services/funnel/funnel-ownership.service';
import {
  PersonalUseTypeEnum,
  PropertyTypeEnum,
} from './funnel-estate-shared.types';

/**
 * Extrait l'ID du propriétaire depuis la liste des owners
 *
 * Logique :
 * - Si aucun owner : retourne defaultId
 * - Si un seul owner : retourne son ownerId
 * - Si plusieurs owners : retourne 'COMMON'
 *
 * @param owners Liste des propriétaires
 * @param defaultId ID par défaut si liste vide
 * @returns ID du propriétaire ou 'COMMON'
 *
 * @example
 * extractOwnerId([{ ownerId: 123 }], 456) // 123
 * extractOwnerId([{ ownerId: 123 }, { ownerId: 124 }], 456) // 'COMMON'
 * extractOwnerId([], 456) // 456
 */
export function extractOwnerId(
  owners: OwnerDto[] | undefined | null,
  defaultId: number,
): number | 'COMMON' {
  if (!owners || owners.length === 0) {
    return defaultId;
  }
  if (owners.length > 1) {
    return 'COMMON';
  }
  return owners[0].ownerId;
}

/**
 * Normalise le personalUseType pour l'affichage UI
 *
 * Transformation :
 * RESIDENCE_SECOND + HOUSE → RESIDENCE_SECOND_HOUSE
 * RESIDENCE_SECOND + APARTMENT → RESIDENCE_SECOND_APARTMENT
 * Autres cas → pas de changement
 *
 * Cette logique existe car le backend n'a qu'un seul type RESIDENCE_SECOND,
 * mais le frontend distingue maison et appartement pour l'UX.
 *
 * @param personalUseType Type de bien (donnée backend)
 * @param propertyType Type de propriété (donnée backend)
 * @returns Type normalisé (donnée frontend)
 *
 * @example
 * normalizePersonalUseTypeForDisplay(PersonalUseTypeEnum.RESIDENCE_SECOND, PropertyTypeEnum.HOUSE) // 'RESIDENCE_SECOND_HOUSE'
 * normalizePersonalUseTypeForDisplay('GARAGE', 'PARKING') // 'GARAGE'
 */
export function normalizePersonalUseTypeForDisplay(
  personalUseType: string,
  propertyType: string | undefined,
): PersonalUseTypeEnum {
  if (
    personalUseType === PersonalUseTypeEnum.RESIDENCE_SECOND &&
    propertyType
  ) {
    if (propertyType === PropertyTypeEnum.HOUSE) {
      return PersonalUseTypeEnum.RESIDENCE_SECOND_HOUSE;
    } else if (propertyType === PropertyTypeEnum.APARTMENT) {
      return PersonalUseTypeEnum.RESIDENCE_SECOND_APARTMENT;
    }
  }
  return personalUseType as PersonalUseTypeEnum;
}

/**
 * Normalise le personalUseType pour le backend
 *
 * Transformation inverse de normalizePersonalUseTypeForDisplay :
 * RESIDENCE_SECOND_HOUSE → { personalUseType: PersonalUseTypeEnum.RESIDENCE_SECOND, propertyType: PropertyTypeEnum.HOUSE }
 * RESIDENCE_SECOND_APARTMENT → { personalUseType: PersonalUseTypeEnum.RESIDENCE_SECOND, propertyType: PropertyTypeEnum.APARTMENT }
 * Autres cas → { personalUseType: inchangé, propertyType: undefined }
 *
 * @param personalUseType Type de bien (donnée frontend)
 * @returns Objet avec personalUseType et propertyType (données backend)
 *
 * @example
 * normalizePersonalUseTypeForBackend(PersonalUseTypeEnum.RESIDENCE_SECOND_HOUSE)
 * // { personalUseType: PersonalUseTypeEnum.RESIDENCE_SECOND, propertyType: PropertyTypeEnum.HOUSE }
 */
export function normalizePersonalUseTypeForBackend(
  personalUseType: PersonalUseTypeEnum | string,
): { personalUseType: string; propertyType?: string } {
  if (personalUseType === PersonalUseTypeEnum.RESIDENCE_SECOND_HOUSE) {
    return {
      personalUseType: PersonalUseTypeEnum.RESIDENCE_SECOND,
      propertyType: PropertyTypeEnum.HOUSE,
    };
  } else if (
    personalUseType === PersonalUseTypeEnum.RESIDENCE_SECOND_APARTMENT
  ) {
    return {
      personalUseType: PersonalUseTypeEnum.RESIDENCE_SECOND,
      propertyType: PropertyTypeEnum.APARTMENT,
    };
  }
  return {
    personalUseType,
    propertyType: undefined,
  };
}

/**
 * Extrait le code pays depuis un objet country
 *
 * Le backend retourne parfois un objet { id, value }, parfois juste une string.
 * Cette fonction normalise les deux cas.
 *
 * @param country Code pays (string) ou objet { id, value }
 * @returns Code pays en string ou chaîne vide
 *
 * @example
 * extractCountryCode('FR') // 'FR'
 * extractCountryCode({ id: 'FR', value: 'France' }) // 'FR'
 * extractCountryCode({ value: 'FR' }) // 'FR'
 * extractCountryCode(null) // ''
 */
export function extractCountryCode(
  country: string | { id?: string; value?: string } | null | undefined,
): string {
  if (!country) return '';
  if (typeof country === 'string') return country;
  return country.id || country.value || '';
}

/**
 * Récupère le prix d'achat d'un bien immobilier
 *
 * @param entity Objet contenant purchasePrice
 * @returns Prix d'achat
 */
export function getPurchasePrice(entity: {
  purchasePrice?: number;
}): number | undefined {
  if (entity.purchasePrice === undefined || entity.purchasePrice === null) {
    return undefined;
  }
  return entity.purchasePrice;
}

/**
 * Convertit un montant string en number
 *
 * Les formulaires Angular retournent parfois des strings pour les montants.
 * Cette fonction normalise en number.
 *
 * @param amount Montant (string ou number)
 * @returns Montant en number
 *
 * @example
 * normalizeAmount('1000') // 1000
 * normalizeAmount(1000) // 1000
 * normalizeAmount('') // 0
 * normalizeAmount(null) // 0
 */
export function normalizeAmount(
  amount: string | number | null | undefined,
): number | null {
  if (amount === null || amount === undefined) return null;
  if (typeof amount === 'number') return amount;
  if (!amount) return 0;
  const parsed = parseFloat(amount);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Extrait l'ID d'un objet ou retourne l'ID directement
 *
 * Utile pour les champs de formulaire qui peuvent contenir soit un ID, soit un objet complet.
 *
 * @param value ID (number) ou objet avec propriété id
 * @returns ID en number ou null
 *
 * @example
 * extractId(123) // 123
 * extractId({ id: 123, name: 'Test' }) // 123
 * extractId(null) // null
 */
export function extractId(
  value: number | { id?: number } | null | undefined,
): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  return value.id || null;
}

/**
 * Extrait l'investmentId d'un estate (selon plusieurs sources possibles)
 *
 * L'investmentId peut être stocké à différents endroits selon le type d'estate :
 * - rentalInvestmentId pour les biens locatifs
 * - investmentId pour les autres types de biens
 * - owners[0].investmentId pour les résidences principales
 *
 * @param estate Estate dont on veut extraire l'investmentId
 * @returns investmentId ou null si non trouvé
 *
 * @example
 * extractInvestmentId({ rentalInvestmentId: 123 }) // 123
 * extractInvestmentId({ investmentId: 456 }) // 456
 * extractInvestmentId({ owners: [{ investmentId: 789 }] }) // 789
 * extractInvestmentId({}) // null
 */
export function extractInvestmentId(estate: any): number | null {
  if (estate.rentalInvestmentId) {
    return estate.rentalInvestmentId;
  } else if (estate.investmentId) {
    return estate.investmentId;
  } else if (estate.owners?.length > 0 && estate.owners[0].investmentId) {
    return estate.owners[0].investmentId;
  }
  return null;
}

/**
 * Bail en cours d'un bien : déjà commencé, sans date de fin ou avec une date de
 * fin future, le plus récemment commencé. `undefined` si le bien n'a aucun bail en cours.
 */
export function findCurrentRent<
  T extends { id?: any; startDate?: any; endDate?: any },
>(rents: T[] | undefined, now: Date = new Date()): T | undefined {
  return (rents || [])
    .filter(rent => !rent.endDate || new Date(rent.endDate) > now)
    .filter(rent => !rent.startDate || new Date(rent.startDate) <= now)
    .sort(
      (a, b) =>
        new Date(b.startDate || 0).getTime() -
        new Date(a.startDate || 0).getTime(),
    )[0];
}

export function findCurrentExpense<
  T extends { type?: any; startDate?: any; endDate?: any },
>(expenses: T[] | undefined, type: any): T | undefined {
  return findCurrentRent(
    (expenses || []).filter(expense => expense.type === type),
  );
}

const PERIODS_PER_YEAR: Record<string, number> = {
  DAILY: 365,
  WEEKLY: 365 / 7,
  MENS: 12,
  THREEMENS: 4,
  SEMESTER: 2,
  ANNU: 1,
};

export function toAnnualAmount(amount: any, frequency: any): number {
  const value = typeof amount === 'number' ? amount : parseFloat(amount);
  const periods = PERIODS_PER_YEAR[frequency];
  return periods ? value * periods : value;
}

export function toMonthlyAmount(amount: any, frequency: any): number {
  return PERIODS_PER_YEAR[frequency]
    ? toAnnualAmount(amount, frequency) / 12
    : toAnnualAmount(amount, frequency);
}

export function roundToCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function isSameAmount(left: any, right: any): boolean {
  const leftValue = typeof left === 'number' ? left : parseFloat(left);
  const rightValue = typeof right === 'number' ? right : parseFloat(right);
  return (
    Number.isFinite(leftValue) &&
    Number.isFinite(rightValue) &&
    Math.round(leftValue * 100) === Math.round(rightValue * 100)
  );
}
